import type { ExportTableOptions } from '../schemas/export.js';
import type { ExportModel } from './build-export-model.js';
import {
  CSS_PX_PER_PT,
  DEFAULT_COLUMN_WIDTH_PX,
  DENSITY_PADDING_PX,
  LINE_HEIGHT_FACTOR,
} from './page-geometry.js';

export const AVG_CHAR_WIDTH_FACTOR = 0.52;
export const BULLET_INDENT_PX = 20;

/**
 * How many wrapped display lines one bullet-or-paragraph line takes at a given
 * cell width — the char-wrapping heuristic shared by `estimateRowHeights` (grid
 * cells, max across columns) and `transposed.ts` (single-column cells, summed).
 * Extracted so both keep exactly the same math rather than two copies drifting.
 */
export function estimateWrappedLineCount(
  text: string,
  hasBullet: boolean,
  widthPx: number,
  paddingPx: number,
  fontPx: number,
): number {
  const indent = hasBullet ? BULLET_INDENT_PX : 0;
  const charsPerLine = Math.max(
    8,
    Math.floor((widthPx - 2 * paddingPx - indent) / (fontPx * AVG_CHAR_WIDTH_FACTOR)),
  );
  return Math.max(1, Math.ceil(text.length / charsPerLine));
}

export function estimateRowHeights(input: {
  model: ExportModel;
  columnWidths: Record<string, number>;
  fontSizePt: number;
  density: keyof typeof DENSITY_PADDING_PX;
}): Record<string, number> {
  const fontPx = input.fontSizePt * CSS_PX_PER_PT;
  const linePx = Math.ceil(fontPx * LINE_HEIGHT_FACTOR);
  const padding = DENSITY_PADDING_PX[input.density];
  const heights: Record<string, number> = {};
  for (const row of input.model.rows) {
    let maxLines = 1;
    for (const column of input.model.columns) {
      const width = input.columnWidths[String(column.number)] ?? DEFAULT_COLUMN_WIDTH_PX;
      const lines = input.model.cellContent[row.key]?.[column.number] ?? [];
      let count = 0;
      for (const line of lines) {
        count += estimateWrappedLineCount(line.text, line.hasBullet, width, padding, fontPx);
      }
      maxLines = Math.max(maxLines, Math.max(1, count));
    }
    heights[`${row.kind}:${row.key}`] = maxLines * linePx + 2 * padding;
  }
  return heights;
}

/**
 * The single place `table.rowHeightMode` (spec R2) is turned into the row
 * heights a document is laid out with — the same role `documentHeaderVisibility`
 * plays for the header band.
 *
 * Every surface that paginates an export must resolve this identically: the
 * backend route (which renders the PDF), the preview's client-side tiling and
 * page layouts (which position the annotation overlay and hit-test the ink
 * anchors), and the export page's own tiling (which drives the repeat-headers
 * and legibility hints). Resolving it twice is how a preview ends up describing
 * a different document from the one the server renders — and with `'contenu'`
 * as the wire default, a second derivation reading `viewState.rowHeights`
 * drifts on the very first export.
 *
 * `columnWidths` must be the *merged* widths (`viewState.columnWidths` with the
 * request's `table.columnWidths` overrides on top) — the same widths the
 * document is rendered with, since the wrapping estimate depends on them.
 */
export function resolveExportRowHeights(input: {
  model: ExportModel;
  table: ExportTableOptions;
  fontSizePt: number;
  columnWidths: Record<string, number>;
  viewStateRowHeights: Record<string, number>;
}): Record<string, number> {
  if (input.table.rowHeightMode === 'contenu') {
    return estimateRowHeights({
      model: input.model,
      columnWidths: input.columnWidths,
      fontSizePt: input.fontSizePt,
      density: input.table.density,
    });
  }
  return input.viewStateRowHeights;
}
